import { useCallback, useEffect, useState } from 'react';
import { Get, Post, Send } from '../utils/RemoteAuthority';
import { Ago, SetTitle } from '../utils/Lobby';
import TopBar from './TopBar';
import './Lobby.css';
const { Modal } = require('react-bootstrap');

// The admin page (the server's /admin routes, for the logins in its ADMIN_LOGINS): the sign-up code, then in tabs of
// their own everyone who has an account (deleting them, giving them a new password) and every game (renaming or
// deleting it). Anyone else is told it is not
// for them.
function Admin()
{
    const [users, setUsers] = useState(null);
    const [games, setGames] = useState(null);
    const [error, setError] = useState(null);
    // The dialog open: { kind: 'deleteUser' | 'password' | 'renameGame' | 'deleteGame', user or game }
    const [dialog, setDialog] = useState(null);
    // Which list: the players or the games (each can grow long)
    const [tab, setTab] = useState('players');

    const load = useCallback(() => {
        Promise.all([Get('/admin/users'), Get('/admin/games')])
            .then(([users, games]) => {
                const failed = [users, games].find(result => result.ok === false);
                if (failed) return setError(failed.error);
                setUsers(users);
                setGames(games);
            })
            .catch(() => setError('The game server is not answering.'));
    }, []);
    useEffect(() => {
        SetTitle('Admin · Through the Ages');
        load();
    }, [load]);
    const close = (changed) => {
        setDialog(null);
        if (changed) load();
    };

    return <div className="tta-lobby">
        <TopBar page="admin"/>
        <main className="tta-lobby-main">
            <h1 className="tta-lobby-heading">Admin</h1>
            {error && <div className="tta-lobby-error">{error}</div>}
            {!users && !error && <div className="tta-muted">Loading…</div>}
            {users && <>
                <SignUpCode/>

                <div className="tta-segmented tta-stats-tabs tta-admin-tabs">
                    <button type="button" className={tab === 'players' ? 'on' : ''} onClick={() => setTab('players')}>Players <span className="tta-muted">{users.length}</span></button>
                    <button type="button" className={tab === 'games' ? 'on' : ''} onClick={() => setTab('games')}>Games <span className="tta-muted">{games.length}</span></button>
                </div>

                {tab === 'players' && <section className="tta-lobby-section">
                    <div className="tta-panel tta-table-wrap">
                        <table className="tta-stats-table tta-admin-table">
                            <thead><tr>
                                <th>Player</th><th>Joined</th><th>Last active</th><th>Games</th><th>Signed in</th><th></th>
                            </tr></thead>
                            <tbody>
                                {users.map(user => <tr key={user.login} className={user.you ? 'me' : ''}>
                                    <td>
                                        <span className="tta-admin-name">{user.name}</span>
                                        {user.admin && <span className="tta-badge tta-admin-badge">admin</span>}
                                        <div className="tta-muted">{user.login}</div>
                                    </td>
                                    <td title={user.createdAt}>{Ago(user.createdAt)}</td>
                                    <td title={user.lastSeen}>{Ago(user.lastSeen)}</td>
                                    <td>{user.games}{user.playing > 0 && <span className="tta-muted"> ({user.playing} going)</span>}</td>
                                    <td>{user.hasPassword ? `${user.sessions} browser${user.sessions === 1 ? '' : 's'}` : <span className="tta-muted">no password</span>}</td>
                                    <td><div className="tta-admin-actions">
                                        {user.hasPassword && <button className="tta-ghost-button" onClick={() => setDialog({ kind: 'password', user })}>New password</button>}
                                        {!user.you && <button className="tta-ghost-button tta-danger-button" onClick={() => setDialog({ kind: 'deleteUser', user })}>Delete</button>}
                                    </div></td>
                                </tr>)}
                            </tbody>
                        </table>
                    </div>
                </section>}

                {tab === 'games' && <section className="tta-lobby-section">
                    {games.length === 0 ? <div className="tta-muted">No games yet.</div> :
                        <div className="tta-panel tta-table-wrap">
                            <table className="tta-stats-table tta-admin-table">
                                <thead><tr><th>Game</th><th>Players</th><th>Now</th><th>Started</th><th>Last move</th><th></th></tr></thead>
                                <tbody>
                                    {games.map(game => <tr key={game.id}>
                                        <td><span className="tta-admin-name">{game.title}</span><div className="tta-muted">#{game.id}</div></td>
                                        <td className="tta-admin-players">{game.players.map((player, seat) =>
                                            <span key={seat} className={player.resigned ? 'resigned' : ''} title={player.login ?? 'anyone (test game)'}>{player.name}</span>)}</td>
                                        <td>{game.finished ? 'Over' : `Round ${game.round}, ${game.status.name}`}
                                            <div className="tta-muted">{game.status.label}</div></td>
                                        <td title={game.createdAt}>{Ago(game.createdAt)}</td>
                                        <td title={game.updatedAt}>{game.version > 0 ? Ago(game.updatedAt) : <span className="tta-muted">none yet</span>}</td>
                                        <td><div className="tta-admin-actions">
                                            <a className="tta-ghost-button" href={`./?game=${game.id}`}>Open</a>
                                            <button className="tta-ghost-button" onClick={() => setDialog({ kind: 'renameGame', game })}>Rename</button>
                                            <button className="tta-ghost-button tta-danger-button" onClick={() => setDialog({ kind: 'deleteGame', game })}>Delete</button>
                                        </div></td>
                                    </tr>)}
                                </tbody>
                            </table>
                        </div>}
                </section>}
            </>}
        </main>
        {dialog?.kind === 'deleteUser' && <DeleteUserDialog user={dialog.user} OnClose={close}/>}
        {dialog?.kind === 'password' && <PasswordDialog user={dialog.user} OnClose={close}/>}
        {dialog?.kind === 'renameGame' && <RenameGameDialog game={dialog.game} OnClose={close}/>}
        {dialog?.kind === 'deleteGame' && <DeleteGameDialog game={dialog.game} OnClose={close}/>}
    </div>;
}

// The sign-up code: shown, changed at once (no restart), or none at all; or back to the server's SIGNUP_CODE
function SignUpCode()
{
    const [current, setCurrent] = useState(null);
    const [code, setCode] = useState('');
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const show = (result) => {
        if (result.ok === false) return setError(result.error);
        setError(null);
        setCurrent(result);
        setCode(result.code ?? '');
    };
    useEffect(() => { Get('/admin/signup-code').then(show).catch(() => setError('The game server is not answering.')); }, []);
    const save = async (value, message) => {
        const result = await Send('PUT', '/admin/signup-code', { code: value }).catch(() => ({ ok: false, error: 'The game server is not answering.' }));
        show(result);
        if (result.ok !== false) setNote(message);
    };
    const changed = current && code.trim() !== (current.code ?? '');
    return <section className="tta-lobby-section">
        <h2 className="tta-lobby-section-title">Sign-up code</h2>
        <div className="tta-panel tta-admin-code">
            {current && <>
                <div className="tta-field-row">
                    <input className="tta-input" value={code} placeholder="No code: anyone with the address can sign up"
                        onChange={(e) => { setCode(e.target.value); setNote(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && changed && save(code, 'Saved: new sign-ups need this code from now on.')}/>
                    <button className="tta-primary-button" disabled={!changed}
                        onClick={() => save(code, code.trim() ? 'Saved: new sign-ups need this code from now on.' : 'Saved: anyone with the address can sign up now.')}>Save</button>
                </div>
                <div className="tta-muted tta-field-note">
                    {current.from === 'page' ? 'Set here.' : current.from === 'environment' ? 'From SIGNUP_CODE in the server\'s settings (.env), until you change it here.' : 'None set.'}
                    {' '}Give it only to the people you play with; existing accounts keep working when it changes.
                    {!current.code && <strong className="tta-admin-warning"> Without a code anyone who finds the address can sign up.</strong>}
                    {current.from === 'page' && <> <button type="button" className="tta-link-button" onClick={() => save(null, 'Back to SIGNUP_CODE from the server\'s settings.')}>Use SIGNUP_CODE instead</button></>}
                </div>
            </>}
            {note && <div className="tta-admin-note">{note}</div>}
            {error && <div className="tta-lobby-error">{error}</div>}
        </div>
    </section>;
}

// A dialog asking to confirm something that cannot be undone
function Confirm({ title, children, action, danger = true, busy, error, OnConfirm, OnClose, footer })
{
    return <Modal show onHide={() => OnClose(false)} centered dialogClassName="tta-new-game-dialog tta-admin-dialog">
        <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
        <Modal.Body className="tta-lobby">
            {children}
            {error && <div className="tta-lobby-error">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
            {footer ?? <>
                <button className="tta-ghost-button" onClick={() => OnClose(false)}>Cancel</button>
                <button className={`tta-primary-button${danger ? ' tta-danger-fill' : ''}`} disabled={busy} onClick={OnConfirm}>{action}</button>
            </>}
        </Modal.Footer>
    </Modal>;
}

// Runs a request for a dialog: busy while it goes, its error if it fails, then(result) if it works
const useRequest = () => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const run = async (request, then) => {
        setBusy(true);
        setError(null);
        const result = await request().catch(() => ({ ok: false, error: 'The game server is not answering.' }));
        setBusy(false);
        result.ok === false ? setError(result.error) : then(result);
    };
    return { busy, error, run };
};

function DeleteUserDialog({ user, OnClose })
{
    const { busy, error, run } = useRequest();
    return <Confirm title={`Delete ${user.name}?`} action={`Delete ${user.login}`} busy={busy} error={error} OnClose={OnClose}
        OnConfirm={() => run(() => Send('DELETE', `/admin/users/${encodeURIComponent(user.login)}`), () => OnClose(true))}>
        <p><strong>{user.name}</strong> ({user.login}) is signed out everywhere and can't sign in again. This can't be undone.</p>
        <ul className="tta-admin-list">
            {user.playing > 0
                ? <li>In the {user.playing} game{user.playing === 1 ? '' : 's'} still going they count as resigned, at once: the game goes on without
                    them (a two-player game ends, the other player winning).</li>
                : <li>They're in no games still going.</li>}
            <li>Finished games keep their results, with their name in them.</li>
            <li>The username {user.login} stays taken, so no one else's record mixes with theirs.</li>
        </ul>
    </Confirm>;
}

function PasswordDialog({ user, OnClose })
{
    const { busy, error, run } = useRequest();
    const [password, setPassword] = useState('');
    // The password made up, shown once
    const [made, setMade] = useState(null);
    const [done, setDone] = useState(false);
    const submit = (chosen) => run(() => Post(`/admin/users/${encodeURIComponent(user.login)}/password`, chosen ? { password: chosen } : {}),
        (result) => { setMade(result.password ?? null); setDone(true); });
    if (done)
    {
        return <Confirm title={`New password for ${user.name}`} OnClose={() => OnClose(true)}
            footer={<button className="tta-primary-button" onClick={() => OnClose(true)}>Done</button>}>
            {made ? <>
                <p>Pass this on to {user.name}. It isn't shown again.</p>
                <div className="tta-admin-password">
                    <code>{made}</code>
                    <button className="tta-ghost-button" onClick={() => navigator.clipboard?.writeText(made)}>Copy</button>
                </div>
            </> : <p>{user.name}'s password is now the one you typed.</p>}
            <p className="tta-muted">They have been signed out everywhere{user.you ? ' else' : ''}, and sign in with the new password (username {user.login}).</p>
        </Confirm>;
    }
    return <Confirm title={`New password for ${user.name}`} danger={false} busy={busy} error={error} OnClose={OnClose}
        footer={<>
            <button className="tta-ghost-button" onClick={() => OnClose(false)}>Cancel</button>
            <button className="tta-ghost-button" disabled={busy} onClick={() => submit(null)}>Make one up</button>
            <button className="tta-primary-button" disabled={busy || password.length < 8} onClick={() => submit(password)}>Set this password</button>
        </>}>
        <p>Their old password stops working and they're signed out everywhere{user.you ? ' else' : ''}.</p>
        <label className="tta-field">
            <span className="tta-field-label">New password</span>
            <input className="tta-input" type="text" value={password} autoComplete="off" placeholder="At least 8 characters"
                onChange={(e) => setPassword(e.target.value)}/>
            <span className="tta-muted tta-field-note">Or make one up: it's shown to you once, to pass on.</span>
        </label>
    </Confirm>;
}

// A game's new title, for everyone in it
function RenameGameDialog({ game, OnClose })
{
    const { busy, error, run } = useRequest();
    const [title, setTitle] = useState(game.title);
    const ready = title.trim() !== '' && title.trim() !== game.title && !busy;
    const submit = () => ready && run(() => Send('PUT', `/admin/games/${game.id}/title`, { title }), () => OnClose(true));
    return <Confirm title={`Rename ${game.title}`} danger={false} action="Rename" busy={!ready} error={error} OnClose={OnClose} OnConfirm={submit}>
        <label className="tta-field">
            <span className="tta-field-label">Title</span>
            <input className="tta-input" value={title} maxLength={60}
                onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()}/>
            <span className="tta-muted tta-field-note">Its players see the new title in their list of games, and on the game's page once it's reloaded.</span>
        </label>
    </Confirm>;
}

function DeleteGameDialog({ game, OnClose })
{
    const { busy, error, run } = useRequest();
    return <Confirm title={`Delete ${game.title}?`} action={`Delete ${game.title}`} busy={busy} error={error} OnClose={OnClose}
        OnConfirm={() => run(() => Send('DELETE', `/admin/games/${game.id}`), () => OnClose(true))}>
        <p><strong>{game.title}</strong> (#{game.id}), between {game.players.map(player => player.name).join(', ')}
            {game.finished ? ', which is over' : `, now in round ${game.round}`}, is deleted with its whole history. This can't be undone.</p>
        <p className="tta-muted">It goes from everyone's list of games{game.finished ? ' and from their stats' : ''}.</p>
    </Confirm>;
}

export default Admin;
